import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ConfigService } from './common/config.service';
import { 
  AiSettings, 
  AiConnection, 
  AiChatSettings,
  AiChatSettingsDetail,
  AiAssessmentSettings,
  AiAssessmentSettingsDetail,
  AiFeatureSettings, 
  AiFeatureSettingsDetail, 
  AiFeature 
} from '../models/chat.models';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class AiSettingsService {
  private connectionsUrl: string;
  private settingsUrl: string;
  private legacyUrl: string;

  constructor(
    private http: HttpClient,
    configService: ConfigService
  ) {
    this.connectionsUrl = environment.apiUrl + '/admin/ai-connections';
    this.settingsUrl = environment.apiUrl + '/admin/ai-settings';
    this.legacyUrl = environment.apiUrl + '/admin/ai-settings'; // For backward compatibility
  }

  // ==================== AI Connections ====================

  /**
   * Get all AI connection configurations
   */
  getAllConnections(): Observable<AiConnection[]> {
    return this.http.get<AiConnection[]>(this.connectionsUrl);
  }

  /**
   * Get a specific AI connection by ID
   */
  getConnectionById(id: number): Observable<AiConnection> {
    return this.http.get<AiConnection>(`${this.connectionsUrl}/${id}`);
  }

  /**
   * Create a new AI connection
   */
  createConnection(connection: AiConnection): Observable<AiConnection> {
    return this.http.post<AiConnection>(this.connectionsUrl, connection);
  }

  /**
   * Update an existing AI connection
   */
  updateConnection(connection: AiConnection): Observable<AiConnection> {
    return this.http.put<AiConnection>(`${this.connectionsUrl}/${connection.id}`, connection);
  }

  /**
   * Delete an AI connection
   */
  deleteConnection(id: number): Observable<void> {
    return this.http.delete<void>(`${this.connectionsUrl}/${id}`);
  }

  // ==================== AI Feature Settings ====================

  /**
   * Get all feature settings
   */
  getAllFeatureSettings(): Observable<AiFeatureSettingsDetail[]> {
    return this.http.get<AiFeatureSettingsDetail[]>(this.settingsUrl);
  }

  /**
   * Get active settings for a specific feature
   */
  getActiveSettingsForFeature(feature: AiFeature): Observable<AiFeatureSettingsDetail> {
    return this.http.get<AiFeatureSettingsDetail>(`${this.settingsUrl}/${feature}`);
  }

  /**
   * Save (create or update) feature settings
   */
  saveFeatureSettings(feature: AiFeature, settings: AiFeatureSettings): Observable<AiFeatureSettingsDetail> {
    return this.http.put<AiFeatureSettingsDetail>(`${this.settingsUrl}/${feature}`, settings);
  }

  /**
   * Delete feature settings
   */
  deleteFeatureSettings(id: number): Observable<void> {
    return this.http.delete<void>(`${this.settingsUrl}/${id}`);
  }

  // ==================== AI Chat Settings ====================

  /**
   * Get active chat settings
   */
  getActiveChatSettings(): Observable<AiChatSettingsDetail> {
    return this.http.get<AiChatSettingsDetail>(`${environment.apiUrl}/admin/ai-chat-settings/active`);
  }

  /**
   * Save chat settings
   */
  saveChatSettings(settings: AiChatSettings): Observable<AiChatSettingsDetail> {
    return this.http.put<AiChatSettingsDetail>(`${environment.apiUrl}/admin/ai-chat-settings`, settings);
  }

  // ==================== AI Assessment Settings ====================

  /**
   * Get active assessment settings
   */
  getActiveAssessmentSettings(): Observable<AiAssessmentSettingsDetail> {
    return this.http.get<AiAssessmentSettingsDetail>(`${this.settingsUrl}/assessments/active`);
  }

  /**
   * Get all assessment settings versions (summary)
   */
  getAllAssessmentVersions(): Observable<any[]> {
    return this.http.get<any[]>(`${this.settingsUrl}/assessments/versions`);
  }

  /**
   * Get specific assessment settings version by ID
   */
  getAssessmentVersionById(id: number): Observable<AiAssessmentSettingsDetail> {
    return this.http.get<AiAssessmentSettingsDetail>(`${this.settingsUrl}/assessments/versions/${id}`);
  }

  /**
   * Activate a specific assessment settings version (rollback)
   */
  activateAssessmentVersion(id: number): Observable<any> {
    return this.http.put<any>(`${this.settingsUrl}/assessments/versions/${id}/activate`, {});
  }

  /**
   * Save assessment settings
   */
  saveAssessmentSettings(settings: AiAssessmentSettings): Observable<AiAssessmentSettingsDetail> {
    return this.http.put<AiAssessmentSettingsDetail>(`${this.settingsUrl}/assessments`, settings);
  }

  // ==================== Legacy (Backward Compatibility) ====================

  /**
   * Get the active AI settings configuration (legacy)
   * @deprecated Use getActiveSettingsForFeature(AiFeature.AIChat) instead
   */
  getSettings(): Observable<AiSettings> {
    return this.http.get<AiSettings>(this.legacyUrl);
  }

  /**
   * Update AI settings configuration (legacy)
   * @deprecated Use saveFeatureSettings instead
   */
  updateSettings(settings: AiSettings): Observable<AiSettings> {
    return this.http.put<AiSettings>(this.legacyUrl, settings);
  }
}

